import React, { useState, useEffect, useCallback, useRef } from 'react';
import { T, FONTS, SHADOW } from '../../constants/theme.js';
import { fetchFolio, postCharge, voidCharge, recordPayment, reversePayment,
  refundPayment, fetchReservation, fetchFolioReceipt, resolveCorporateTariff } from '../../services/api.js';
import { Card, Btn, Modal, Field, Input, Select, Badge, fmtCurrency, fmtDate,
  fmtDateTime, Toast, SectionHeader, Spinner, useOfflineInvalidation } from '../../components/ui.jsx';
import { openPrintWindow, resolveAssetUrl } from '../../utils/printWindow.js';
import InvoicePrint from '../../components/folio/InvoicePrint.jsx';
import PaymentRequestPrint from '../../components/folio/PaymentRequestPrint.jsx';
import { splitAccommodationTax, computeInvoiceTaxFromCharges } from '../../utils/invoiceUtils.js';
// warmCacheForView is a no-op on desktop (SQLite kept warm by SyncWorker)
// warmCacheForView: no-op in desktop build (SyncWorker handles cache warming)
const warmCacheForView = () => {};

const CHARGE_TYPES = [
  {id:'room',label:'Room Charge',icon:'🛏️'},{id:'restaurant',label:'Restaurant',icon:'🍽️'},
  {id:'gym',label:'Gym',icon:'🏋️'},{id:'swimming',label:'Swimming Pool',icon:'🏊'},
  {id:'conference',label:'Conference Hall',icon:'🏢'},
  {id:'spa',label:'Spa & Wellness',icon:'💆'},{id:'minibar',label:'Minibar',icon:'🍷'},
  {id:'parking',label:'Parking',icon:'🚗'},{id:'laundry',label:'Laundry',icon:'👕'},
  {id:'phone',label:'Phone / Internet',icon:'📞'},{id:'transport',label:'Airport Transfer',icon:'🚌'},
  {id:'tax',label:'Tax / Levy',icon:'🏛️'},{id:'other',label:'Miscellaneous',icon:'📦'},
];

const PAYMENT_METHODS = [
  {id:'cash',label:'Cash',icon:'💵'},{id:'visa',label:'Visa Card',icon:'💳'},
  {id:'mastercard',label:'Mastercard',icon:'💳'},{id:'mtn_momo',label:'MTN MoMo',icon:'📱'},
  {id:'airtel',label:'Airtel Money',icon:'📲'},{id:'bank',label:'Bank Transfer',icon:'🏦'},
  {id:'voucher',label:'Voucher / City Ledger',icon:'🎟️'},{id:'comp',label:'Complimentary',icon:'🎁'},
];

// ─── Category-Based Tax Engine (client-side preview) ───────────────────────────
// Mirrors backend CategoryBasedTaxEngine: room charges are Accommodation,
// taxed at BOTH Property.tourismTaxRate AND Property.vatRate combined (e.g.
// 3% + 18%); everything else is Other Services, taxed at Property.vatRate
// only. This is ONLY used to show a live preview in the Add Charge form and
// for offline-mode charges (no server round-trip available). Online, the
// actual tax is always (re)computed authoritatively by the backend at
// posting time — this never overrides that.
//
// The amount staff types in is the full, tax-inclusive price the guest pays
// — it's posted to the backend and stored exactly as entered. Tax here is
// extracted OUT of that amount for display (never added on top), so the
// "Total" shown always equals the amount typed in.
function previewTax(chargeType, amount, property) {
  const amt = parseFloat(amount) || 0;
  const isAccommodation = chargeType === 'room';
  const vatRate = Number(property?.vatRate ?? 18);
  if (isAccommodation) {
    const tourismRate = Number(property?.tourismTaxRate ?? 3);
    const combined = tourismRate + vatRate;
    const { tourismTax, vat } = splitAccommodationTax(amt, tourismRate, vatRate);
    return {
      taxCode: 'TOURISM_VAT', taxRate: combined, taxAmount: round2(tourismTax + vat),
      tourismRate, vatRate, tourismTax, vatTax: vat, category: 'Accommodation',
    };
  }
  const taxAmount = vatRate > 0 ? round2(amt * vatRate / (100 + vatRate)) : 0;
  return { taxCode: 'VAT', taxRate: vatRate, taxAmount, tourismRate: 0, vatRate, tourismTax: 0, vatTax: taxAmount, category: 'Other Services' };
}
const round2 = n => Math.round((Number(n) || 0) * 100) / 100;

function ReceiptPrint({ folio, reservation, property, onClose }) {
  const printRef = useRef();
  const [printFormat, setPrintFormat] = useState('pos80'); // 'pos80' | 'letter'
  const charges  = (folio?.charges||[]).filter(c=>!c.voided);
  const payments = (folio?.payments||[]).filter(p=>p.amount>0&&!p.reversed);
  const totalCharges  = charges.reduce((a,c)=>a+parseFloat(c.amount||0),0);
  const taxBreakdown  = computeInvoiceTaxFromCharges(charges, property);
  const totalTax      = taxBreakdown.totalTax;
  const totalPaid     = payments.reduce((a,p)=>a+parseFloat(p.amount||0),0);
  const balance       = totalCharges - totalPaid;
  const curr          = folio?.currency||property?.currency||'USD';
  const resolvedRoomTypeName = reservation?.roomTypeName || folio?.roomTypeName || reservation?.roomType;
  const resolvedRoomNumber   = reservation?.roomNumber   || folio?.roomNumber;
  const roomDisplay   = [resolvedRoomTypeName, resolvedRoomNumber ? `Room ${resolvedRoomNumber}` : null]
    .filter(Boolean).join(' — ') || resolvedRoomNumber || '—';
  const DARK_YELLOW = '#7A5F00';
  const settled = balance === 0;

  const chargeTypeLabel = type => ({
    room:'Room Charge', restaurant:'Restaurant', spa:'Spa & Wellness',
    minibar:'Minibar', parking:'Parking', laundry:'Laundry',
    phone:'Phone/Internet', transport:'Transport', tax:'Tax', other:'Misc.',
  })[type] || type;

  const doPrint = () => {
    const ok = openPrintWindow({
      title: `Receipt — ${reservation?.confirmationNumber || ''}`,
      bodyHtml: printRef.current.innerHTML,
      format: printFormat,
      scale: printFormat === 'letter' ? 1.7 : undefined,
    });
    if (!ok) alert('Your browser blocked the print popup. Please allow popups for this site and try again.');
  };

  /* ── shared style tokens ── */
  const gold   = T.gold;
  const navy   = T.navy;
  const S = {
    sectionLabel: {
      display:'flex', alignItems:'center', gap:7, marginBottom:10,
    },
    sectionPip: {
      width:4, height:14, background:`linear-gradient(180deg,${T.goldLight},${T.gold})`,
      borderRadius:3, display:'inline-block', flexShrink:0,
    },
    sectionTitle: {
      fontSize:10, fontWeight:800, color:navy, letterSpacing:1.6,
      textTransform:'uppercase',
    },
    row: {
      display:'flex', justifyContent:'space-between', alignItems:'center',
      padding:'5px 0', borderBottom:`1px solid ${T.border}`,
    },
    rowLabel: { fontSize:11, fontWeight:700, color:T.slate },
    rowValue: { fontSize:12, fontWeight:800, color:navy },
  };

  return (
    <div>
      {/* ── Screen Preview ── */}
      <div ref={printRef} style={{
        fontFamily:FONTS.body, fontSize:11, maxWidth:302, margin:'0 auto',
        color:navy, background:T.white, borderRadius:12, overflow:'hidden',
        boxShadow:SHADOW.xl, border:`1px solid ${T.border}`,
      }}>

        {/* Top shimmer bar */}
        <div style={{height:5, background:`linear-gradient(90deg,${T.navyDeep},${T.gold},${T.teal},${T.gold},${T.navyDeep})`}} />

        {/* ── Header ── */}
        <div style={{
          background:`linear-gradient(160deg,${T.navy} 0%,${T.navyMid} 60%,${T.navyLight} 100%)`,
          padding:'14px 14px 12px', textAlign:'center',
        }}>
          {property?.logoUrl && (
            <img src={resolveAssetUrl(property.logoUrl)} alt="" crossOrigin="anonymous"
              style={{ maxWidth:84, maxHeight:54, objectFit:'contain', marginBottom:6 }} />
          )}
          <div style={{
            fontFamily:FONTS.display, fontSize:20, fontWeight:700,
            color:T.goldLight, letterSpacing:.6, lineHeight:1.2,
          }}>
            {property?.name||'Hotel'}
          </div>
          <div style={{
            width:36, height:1, margin:'5px auto 6px',
            background:`linear-gradient(90deg,transparent,${T.gold},transparent)`,
          }} />
          <div style={{fontSize:9.5, fontWeight:700, color:'rgba(255,255,255,.80)', lineHeight:1.7}}>
            {property?.addressLine1 && <div>{property.addressLine1}</div>}
            <div>{[property?.city,property?.country].filter(Boolean).join(', ')}</div>
            {property?.phone && <div>Tel: {property.phone}</div>}
            {property?.email && <div>{property.email}</div>}
          </div>
        </div>

        {/* gold bottom-border on header */}
        <div style={{height:3, background:`linear-gradient(90deg,${T.navyDeep},${T.gold},${T.navyDeep})`}} />

        <div style={{padding:'12px 12px 14px'}}>

          {/* ── Receipt badge ── */}
          <div style={{textAlign:'center', marginBottom:10}}>
            <span style={{
              display:'inline-block',
              background:`linear-gradient(135deg,${T.navy},${T.navyLight})`,
              color:T.goldLight, fontSize:9.5, fontWeight:800,
              letterSpacing:1.6, textTransform:'uppercase',
              padding:'4px 14px', borderRadius:99,
              boxShadow:`0 2px 8px rgba(13,27,42,.20)`,
            }}>✦ Guest Folio Receipt ✦</span>
            <div style={{fontSize:9, fontWeight:700, color:T.muted, marginTop:4}}>
              {new Date().toLocaleString('en-GB')}
            </div>
          </div>

          {/* ── Reservation Details card ── */}
          <div style={{
            background:T.surface, borderRadius:8, padding:'9px 10px',
            marginBottom:10, border:`1px solid ${T.border}`,
          }}>
            <div style={S.sectionLabel}>
              <span style={S.sectionPip} />
              <span style={S.sectionTitle}>Reservation Details</span>
            </div>
            {[
              ['Confirmation',  reservation?.confirmationNumber || '—'],
              ['Guest',         reservation?.guestName || '—'],
              ['Room',          roomDisplay],
              ['Meal Plan',     (reservation?.mealPlan||'ROOM ONLY').replace(/_/g,' ')],
              ['Check-In',      fmtDate(reservation?.arrivalDate)],
              ['Check-Out',     fmtDate(reservation?.departureDate)],
              ['Nights',        folio?.numNights ?? reservation?.numNights ?? '—'],
            ].map(([k,v],i)=>(
              <div key={k} style={{
                ...S.row,
                padding:'4px 2px',
              }}>
                <span style={S.rowLabel}>{k}</span>
                <span style={S.rowValue}>{v}</span>
              </div>
            ))}
          </div>

          {/* ── Charges ── */}
          <div style={{marginBottom:10}}>
            <div style={S.sectionLabel}>
              <span style={S.sectionPip} />
              <span style={S.sectionTitle}>Charges</span>
            </div>
            {charges.length === 0
              ? <div style={{fontSize:10, fontWeight:700, color:T.softGrey, textAlign:'center', padding:'6px 0'}}>No charges recorded</div>
              : charges.map((c,i) => {
                  // Re-derive VAT and Tourism Levy from the gross amount using the
                  // RRA two-step formulas so the label is always consistent with
                  // what is shown on printed invoices, regardless of what is stored
                  // in taxAmount (handles both pre- and post-backfill rows correctly).
                  const gross       = parseFloat(c.amount || 0);
                  const isAccom     = c.taxCode === 'TOURISM_VAT';
                  const tRate       = Number(property?.tourismTaxRate ?? 3);
                  const vRate       = Number(property?.vatRate ?? 18);
                  const { vat: cVat, tourismTax: cTourism } = isAccom
                    ? splitAccommodationTax(gross, tRate, vRate)
                    : { vat: 0, tourismTax: 0 };
                  const cVatOnly = !isAccom && parseFloat(c.taxAmount || 0) > 0
                    ? parseFloat(c.taxAmount) : 0;
                  return (
                    <div key={i} style={{
                      display:'flex', justifyContent:'space-between', alignItems:'flex-start',
                      padding:'5px 0', borderBottom:`1px solid ${T.border}`,
                    }}>
                      <div style={{flex:1, marginRight:6}}>
                        <div style={{fontWeight:800, fontSize:10.5, color:navy, lineHeight:1.35}}>{c.description||chargeTypeLabel(c.chargeType)}</div>
                        <div style={{fontSize:9, fontWeight:700, color:T.muted}}>
                          {c.postingDate ? fmtDate(c.postingDate) : ''}
                          {isAccom && cVat > 0 && (
                            <> &nbsp;·&nbsp;VAT {fmtCurrency(cVat,curr)} + Tourism {fmtCurrency(cTourism,curr)}</>
                          )}
                          {!isAccom && cVatOnly > 0 && (
                            <> &nbsp;·&nbsp;VAT {fmtCurrency(cVatOnly,curr)}</>
                          )}
                        </div>
                      </div>
                      <span style={{fontWeight:800, fontSize:11, color:navy, whiteSpace:'nowrap'}}>{fmtCurrency(c.amount,curr)}</span>
                    </div>
                  );
                })
            }
            {/* Tax breakdown */}
            {totalTax > 0 && (
              <div style={{borderTop:`1px dashed ${T.border}`, marginTop:4, paddingTop:4}}>
                <div style={{display:'flex',justifyContent:'space-between',fontSize:10,fontWeight:700,color:T.slate,padding:'2px 0'}}>
                  <span>Subtotal (excl. tax)</span><span>{fmtCurrency(totalCharges-totalTax,curr)}</span>
                </div>
                {taxBreakdown.breakdown.map(b => (
                  <div key={b.code} style={{display:'flex',justifyContent:'space-between',fontSize:10,fontWeight:700,color:T.slate,padding:'2px 0'}}>
                    <span>{b.label} ({b.ratePct}%)</span><span>{fmtCurrency(b.tax,curr)}</span>
                  </div>
                ))}
              </div>
            )}
            {/* Total Charges banner */}
            <div style={{
              display:'flex', justifyContent:'space-between', alignItems:'center',
              fontWeight:800, fontSize:11.5,
              background:`linear-gradient(90deg,${T.navy},${T.navyLight})`,
              color:T.goldLight, padding:'7px 10px', borderRadius:7, marginTop:6,
            }}>
              <span>Total Charges</span>
              <span>{fmtCurrency(totalCharges,curr)}</span>
            </div>
          </div>

          {/* ── Payments ── */}
          <div style={{marginBottom:10}}>
            <div style={S.sectionLabel}>
              <span style={S.sectionPip} />
              <span style={S.sectionTitle}>Payments Received</span>
            </div>
            {payments.length === 0
              ? <div style={{fontSize:10, fontWeight:700, color:T.softGrey, textAlign:'center', padding:'6px 0'}}>No payments recorded</div>
              : payments.map((p,i)=>(
                <div key={i} style={{
                  display:'flex', justifyContent:'space-between', alignItems:'center',
                  padding:'5px 0', borderBottom:`1px solid ${T.border}`,
                }}>
                  <div>
                    <div style={{fontWeight:800, fontSize:10.5, color:navy}}>
                      {(p.method||'').replace(/_/g,' ').replace(/\b\w/g,l=>l.toUpperCase())}
                    </div>
                    {p.paidAt && <div style={{fontSize:9, fontWeight:700, color:T.muted}}>{fmtDate(p.paidAt)}</div>}
                  </div>
                  <span style={{fontWeight:800, fontSize:11, color:T.success}}>{fmtCurrency(p.amount,curr)}</span>
                </div>
              ))
            }
            {/* Total Paid banner */}
            <div style={{
              display:'flex', justifyContent:'space-between', alignItems:'center',
              fontWeight:800, fontSize:11.5,
              background:`linear-gradient(90deg,${T.success},#0E6B3A)`,
              color:T.white, padding:'7px 10px', borderRadius:7, marginTop:6,
            }}>
              <span>Total Paid</span>
              <span>{fmtCurrency(totalPaid,curr)}</span>
            </div>
          </div>

          {/* ── Balance banner — only shown when there is an outstanding or credit amount ── */}
          {balance !== 0 && (
            <div style={{
              background: balance < 0
                ? `linear-gradient(90deg,${T.teal},#0E5C6B)`
                : `linear-gradient(90deg,${T.navy},${T.navyLight})`,
              color:T.white, borderRadius:7, padding:'8px 10px',
              display:'flex', justifyContent:'space-between', alignItems:'center',
              marginBottom:10,
            }}>
              <span style={{fontSize:10.5, fontWeight:800, color:T.goldLight}}>
                {balance > 0 ? '⚠ BALANCE DUE' : '↩ CREDIT BALANCE'}
              </span>
              <span style={{fontSize:13, fontWeight:800}}>{fmtCurrency(Math.abs(balance),curr)}</span>
            </div>
          )}

          {/* ── Footer ── */}
          <div style={{textAlign:'center', fontSize:9.5, borderTop:`1px solid ${T.goldPale}`, paddingTop:8}}>
            <div style={{fontWeight:800, color:navy, fontSize:10.5, marginBottom:1}}>
              Thank you for staying with us
            </div>
            {property?.website && (
              <div style={{fontWeight:700, color:T.muted, marginBottom:4}}>{property.website}</div>
            )}
            <div style={{
              width:40, height:1, margin:'6px auto',
              background:`linear-gradient(90deg,transparent,${T.gold},transparent)`,
            }} />
            <div style={{fontWeight:800, color:DARK_YELLOW, letterSpacing:.5, fontSize:10}}>
              Powered by <span style={{letterSpacing:.8}}>Orinest IHMIS</span>
            </div>
            <div style={{fontWeight:700, color:T.muted, fontSize:9, marginTop:1}}>
              © {new Date().getFullYear()}
            </div>
          </div>

        </div>{/* /padding wrapper */}

        {/* Bottom shimmer bar */}
        <div style={{height:4, background:`linear-gradient(90deg,${T.navyDeep},${T.gold},${T.navyDeep})`}} />
      </div>{/* /printRef */}

      <div style={{ display:'flex', gap:8, marginTop:18 }}>
        {[
          { id:'pos80', label:'🧾 POS (80mm)' },
          { id:'letter', label:'📄 Letter / A4' },
        ].map(o => (
          <button key={o.id} onClick={() => setPrintFormat(o.id)} style={{
            flex:1, padding:'8px 10px', borderRadius:9, cursor:'pointer',
            border:`2px solid ${printFormat===o.id ? T.teal : T.border}`,
            background:printFormat===o.id ? T.tealPale : T.white,
            color:printFormat===o.id ? T.teal : T.muted,
            fontFamily:FONTS.body, fontSize:11, fontWeight:700,
          }}>{o.label}</button>
        ))}
      </div>
      <div style={{display:'flex',gap:10,marginTop:10}}>
        <Btn variant="secondary" onClick={onClose} style={{flex:1}}>Close</Btn>
        <Btn variant="primary" onClick={doPrint} style={{flex:2}}>🖨️ Print Receipt</Btn>
      </div>
    </div>
  );
}


export default function FolioSheet({ reservationId, property, onBack }) {
  const [folio,       setFolio]     = useState(null);
  const [reservation, setRes]       = useState(null);
  const [loading,     setLoad]      = useState(true);
  const [saving,      setSaving]    = useState(false);
  const [toast,       setToast]     = useState('');
  const [showCharge,  setShowCharge]= useState(false);
  const [showPay,     setShowPay]   = useState(false);
  const [showReceipt, setShowReceipt]=useState(false);
  const [showInvoice, setShowInvoice]=useState(false);
  const [showPaymentRequest, setShowPaymentRequest]=useState(false);
  const [showRefund,  setShowRefund] = useState(false);
  const [showReverse, setShowReverse]= useState(null); // payment obj
  const [charge,   setCharge]   = useState({chargeType:'room',description:'',amount:'',taxOverride:false,taxRate:''});
  const isCorporateReservation = reservation?.reservationCategory === 'corporate' && reservation?.corporateAccountId;
  const [corpChargeTariff, setCorpChargeTariff] = useState(null);
  const [corpChargeTariffLoading, setCorpChargeTariffLoading] = useState(false);

  // When this is a Corporate Reservation, resolve the company's negotiated
  // price for whichever service type is selected and prefill the amount —
  // staff can still type over it, but the contracted price is the default.
  useEffect(() => {
    if (!showCharge || !isCorporateReservation) { setCorpChargeTariff(null); return; }
    setCorpChargeTariffLoading(true);
    resolveCorporateTariff(reservation.corporateAccountId, charge.chargeType)
      .then(t => {
        setCorpChargeTariff(t || null);
        if (t) setCharge(p => ({ ...p, amount: String(t.negotiatedPrice) }));
      })
      .catch(() => setCorpChargeTariff(null))
      .finally(() => setCorpChargeTariffLoading(false));
  }, [showCharge, charge.chargeType, isCorporateReservation, reservation?.corporateAccountId]);
  const [payment,  setPayment]  = useState({method:'cash',amount:'',transactionRef:'',notes:''});
  const [refund,   setRefund]   = useState({method:'cash',amount:'',reason:'',reference:''});
  const [momoPhone,setMomoPhone]= useState('');
  const [momoSt,   setMomoSt]  = useState('idle');
  const [folioOffline, setFolioOffline] = useState(false);

  const currency = property?.currency || folio?.currency || 'USD';
  const showToast = msg => { setToast(msg); setTimeout(()=>setToast(''),3500); };

  const load = useCallback(async()=>{
    if(!reservationId)return;
    setLoad(true);
    setFolioOffline(false);
    try{
      const [f,r] = await Promise.all([
        fetchFolio(reservationId).catch(e => {
          if (e?.message?.toLowerCase().includes('offline') || e?.message?.toLowerCase().includes('cache')) {
            setFolioOffline(true);
          }
          return null;
        }),
        fetchReservation(reservationId).catch(()=>null),
      ]);
      setFolio(f); setRes(r);
    }finally{setLoad(false);}
  },[reservationId]);

  useOfflineInvalidation('reservations', load);

  useEffect(()=>{load();},[load]);

  // Warm folio cache whenever this sheet mounts while online so the data
  // is available for the next offline session.
  useEffect(()=>{
    if(reservationId) warmCacheForView('folio', reservationId);
  },[reservationId]);

  const charges      = (folio?.charges||[]).filter(c=>!c.voided);
  const voidedChgs   = (folio?.charges||[]).filter(c=>c.voided);
  const allPayments  = folio?.payments||[];
  const payments     = allPayments.filter(p=>p.amount>0&&!p.reversed);
  const reversals    = allPayments.filter(p=>p.amount<0||p.reversed);
  const totalCharges = charges.reduce((a,c)=>a+parseFloat(c.amount||0),0);
  const totalPaid    = allPayments.reduce((a,p)=>a+parseFloat(p.amount||0),0);
  const balance      = totalCharges - totalPaid;

  const handleCharge = async()=>{
    if(!charge.amount){showToast('Amount is required');return;}
    setSaving(true);
    try{
      const amount=parseFloat(charge.amount);
      // FIX: referenceId is the server-side dedup key for FolioCharge.
      // Without it, a network retry with a new clientUuid double-posts the charge.
      // Generate once per submit so each distinct charge gets a stable key.
      const referenceId = crypto.randomUUID();
      const base = {chargeType:charge.chargeType, description:charge.description,
        amount, currency, referenceId, postingDate:new Date().toISOString().slice(0,10)};
      // Tax is resolved automatically by the backend's category-based tax engine
      // (Accommodation vs Other Services) unless the user explicitly checked
      // "Override tax" — in which case we send taxCode:'MANUAL' so the backend
      // trusts the entered rate instead of recomputing it. The amount itself is
      // always the full, tax-inclusive figure staff typed in — taxAmount here is
      // just the informational portion of it extracted for display, never added on top.
      const overrideRate = parseFloat(charge.taxRate) || 0;
      const payload = charge.taxOverride
        ? {...base, taxCode:'MANUAL', taxRate:overrideRate,
           taxAmount: overrideRate > 0 ? Math.round(amount * overrideRate / (100 + overrideRate) * 100) / 100 : 0}
        : base;
      const r = await postCharge(reservationId, payload);
      showToast(r?._offline ? '⏳ Charge saved offline — will sync when server is back' : '✅ Charge posted');
      setShowCharge(false);
      setCharge({chargeType:'room',description:'',amount:'',taxOverride:false,taxRate:''});
      load();
    }catch(e){showToast(`❌ ${e.message}`);}
    finally{setSaving(false);}
  };

  const handlePayment = async(overrideMethod)=>{
    const method=overrideMethod||payment.method;
    if(!payment.amount){showToast('Amount required');return;}
    setSaving(true);
    try{
      // FIX: transactionRef is the server-side dedup key for FolioPayment.
      // Auto-generate when the user leaves it blank so the backend can detect
      // a duplicate on network retry, preventing double-payment.
      const txRef = (payment.transactionRef || '').trim() || crypto.randomUUID();
      const r = await recordPayment(reservationId,{method,amount:parseFloat(payment.amount),
        currency,transactionRef:txRef,notes:payment.notes,paidAt:new Date().toISOString()});
      showToast(r?._offline ? '⏳ Payment saved offline — will sync when server is back' : '✅ Payment recorded');
      setShowPay(false); setMomoSt('idle');
      setPayment({method:'cash',amount:'',transactionRef:'',notes:''});
      load();
    }catch(e){showToast(`❌ ${e.message}`);}
    finally{setSaving(false);}
  };

  const handleVoid = async(chargeId)=>{
    if(!window.confirm('Void this charge?'))return;
    try{await voidCharge(chargeId);showToast('Charge voided');load();}
    catch(e){showToast(`❌ ${e.message}`);}
  };

  const handleReverse = async(p)=>{
    if(!p)return;
    const reason = showReverse?.reverseReason||'Payment reversed';
    setSaving(true);
    try{
      // action hint lets the offline WAL mapper in httpToSyncEvent.ts
      // produce { entityType:'FOLIO_PAYMENT', action:'REVERSE' } correctly
      const r = await reversePayment(p.id, reason);
      showToast(r?._offline ? '⏳ Reversal saved offline' : '✅ Payment reversed');
      setShowReverse(null); load();
    }catch(e){showToast(`❌ ${e.message}`);}
    finally{setSaving(false);}
  };

  const handleRefund = async()=>{
    if(!refund.amount){showToast('Refund amount required');return;}
    setSaving(true);
    try{
      const r = await refundPayment(reservationId,{
        amount:parseFloat(refund.amount),method:refund.method,
        reason:refund.reason,reference:refund.reference,currency,
      });
      showToast(r?._offline ? '⏳ Refund saved offline' : `✅ Refund of ${fmtCurrency(refund.amount,currency)} recorded`);
      setShowRefund(false);
      setRefund({method:'cash',amount:'',reason:'',reference:''});
      load();
    }catch(e){showToast(`❌ ${e.message}`);}
    finally{setSaving(false);}
  };

  const triggerMoMo=()=>{
    setMomoSt('pending');
    showToast('📱 MoMo push sent — awaiting confirmation…');
    setTimeout(()=>{ setMomoSt('success'); handlePayment(payment.method); },3000);
  };

  if(loading)return<div style={{display:'flex',justifyContent:'center',padding:80}}><Spinner/></div>;

  // Folio cache miss offline — show a friendly message so users know what happened.
  // Do NOT show this screen if the folio has any charges or payments (synthetic
  // offline folio): those should be displayed even without a server folio row.
  if(!folio && folioOffline) return (
    <div className="fade-up">
      <SectionHeader title="🧾 Smart Folio"
        subtitle={reservation?`${reservation.guestName} · ${reservation.confirmationNumber}`:''}
        right={<Btn variant="secondary" onClick={onBack}>← Back</Btn>}
      />
      <div style={{ background:T.surface, border:`2px dashed ${T.border}`, borderRadius:16,
        padding:'40px 24px', textAlign:'center', marginTop:16 }}>
        <div style={{ fontSize:40, marginBottom:12 }}>📋</div>
        <p style={{ fontSize:15, fontWeight:700, color:T.navy, marginBottom:6 }}>
          Folio not available offline
        </p>
        <p style={{ fontSize:12, color:T.muted, maxWidth:340, margin:'0 auto 16px' }}>
          The folio for this reservation was not cached before you went offline.
          Open this reservation while connected to the internet to make the folio available offline.
        </p>
        <Btn variant="primary" onClick={load}>🔄 Retry</Btn>
      </div>
    </div>
  );

  return(
    <div className="fade-up">
      <SectionHeader title="🧾 Smart Folio"
        subtitle={reservation?`${reservation.guestName} · ${reservation.confirmationNumber}`:''}
        right={<>
          <Btn variant="secondary" onClick={onBack}>← Back</Btn>
          {/* Show Receipt unless the folio is synthetic AND the reservation
               itself is still pending (truly offline — not yet synced).
               A folio can be _synthetic even after sync when the server never
               pushed a folio header row; hiding the button in that case blocks
               staff from printing for synced reservations. */}
          {!(folio?._synthetic && reservation?._pending) && (
            <Btn variant="secondary" onClick={()=>setShowReceipt(true)}>🖨️ Receipt</Btn>
          )}
          <Btn variant="secondary" onClick={()=>setShowInvoice(true)}>🧾 Invoice</Btn>
          {reservation?.reservationCategory === 'corporate' && (
            <Btn variant="secondary" onClick={()=>setShowPaymentRequest(true)}>📨 Payment Request</Btn>
          )}
          <Btn variant="ghost" onClick={()=>setShowCharge(true)}>+ Charge</Btn>
          {balance>0&&<Btn variant="secondary" onClick={()=>setShowRefund(true)}>↩️ Refund</Btn>}
          <Btn variant={balance>0?'gold':'secondary'} onClick={()=>setShowPay(true)}>💳 Payment</Btn>
        </>}
      />

      {/* Summary */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:12,marginBottom:20}}>
        {[{label:'Total Charges',value:fmtCurrency(totalCharges,currency),color:T.navy},
          {label:'Total Paid',value:fmtCurrency(totalPaid,currency),color:T.success},
          {label:balance>0?'Balance Due':balance<0?'Credit':'Settled',
           value:fmtCurrency(Math.abs(balance),currency),
           color:balance>0?T.danger:balance<0?T.warning:T.success},
          {label:'Nights',value:(folio?.numNights ?? reservation?.numNights) || '—',color:T.teal},
        ].map(item=>(
          <Card key={item.label} style={{padding:'14px 16px',textAlign:'center'}}>
            <p style={{fontSize:9,fontWeight:700,color:T.muted,letterSpacing:.8,textTransform:'uppercase',marginBottom:6}}>{item.label}</p>
            <p style={{fontSize:22,fontWeight:700,color:item.color,fontFamily:FONTS.display}}>{item.value}</p>
          </Card>
        ))}
      </div>

      {/* Offline pending notice — shown ONLY when the folio is synthetic AND the
           reservation itself is still pending (i.e. the device was offline when the
           reservation was created and hasn't synced yet).
           Previously this showed whenever _synthetic was true, which is the case for
           ANY reservation whose server folio row hasn't been pulled yet — including
           already-synced reservations where the server simply never pushed a folio
           header row.  That caused the banner to appear permanently for those stays. */}
      {folio?._synthetic && reservation?._pending && (
        <div style={{background:'#FFF9EC',border:'1px solid #F59E0B30',borderRadius:10,
          padding:'10px 16px',marginBottom:16,display:'flex',alignItems:'center',gap:10}}>
          <span style={{fontSize:18}}>⏳</span>
          <span style={{fontSize:12,color:'#92400E'}}>
            <strong>Offline folio</strong> — charges and payments added while offline are shown here
            and will sync with the server automatically when you reconnect.
          </span>
        </div>
      )}

      {balance>0&&(
        <div style={{background:T.dangerPale,border:`1px solid ${T.danger}30`,borderRadius:10,
          padding:'10px 16px',marginBottom:16,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
          <span style={{fontSize:13,color:T.danger,fontWeight:600}}>⚠️ Outstanding {fmtCurrency(balance,currency)}</span>
          <Btn variant="danger" size="sm" onClick={()=>setShowPay(true)}>Collect Now</Btn>
        </div>
      )}
      {balance<0&&(
        <div style={{background:T.warningPale,border:`1px solid ${T.warning}30`,borderRadius:10,padding:'10px 16px',marginBottom:16,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
          <span style={{fontSize:13,color:T.warning,fontWeight:600}}>💰 Credit {fmtCurrency(Math.abs(balance),currency)} — refund or apply to future stay</span>
          <Btn variant="secondary" size="sm" onClick={()=>setShowRefund(true)}>↩️ Refund Guest</Btn>
        </div>
      )}

      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16}}>
        {/* Charges */}
        <Card style={{padding:0,overflow:'hidden'}}>
          <div style={{padding:'14px 18px',borderBottom:`1px solid ${T.border}`,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
            <p style={{fontSize:13,fontWeight:700,color:T.navy}}>Charges ({charges.length})</p>
            <Btn size="sm" variant="ghost" onClick={()=>setShowCharge(true)}>+ Add</Btn>
          </div>
          <div style={{overflowY:'auto',maxHeight:380}}>
            {charges.length===0
              ?<p style={{textAlign:'center',padding:30,color:T.muted,fontSize:12}}>No charges posted</p>
              :charges.map((c,i)=>{
                const ct = CHARGE_TYPES.find(t=>t.id===c.chargeType);
                // Derive tax breakdown from gross amount using RRA formulas so
                // display is always correct regardless of stored taxAmount value.
                const gross    = parseFloat(c.amount || 0);
                const isAccom  = c.taxCode === 'TOURISM_VAT';
                const tRate    = Number(property?.tourismTaxRate ?? 3);
                const vRate    = Number(property?.vatRate ?? 18);
                const { vat: dVat, tourismTax: dTourism } = isAccom
                  ? splitAccommodationTax(gross, tRate, vRate)
                  : { vat: 0, tourismTax: 0 };
                const dVatOnly = !isAccom && parseFloat(c.taxAmount || 0) > 0
                  ? parseFloat(c.taxAmount) : 0;
                return(
                  <div key={c.id||i} style={{padding:'11px 18px',borderBottom:`1px solid ${T.border}`,display:'flex',alignItems:'center',gap:10}}>
                    <span style={{fontSize:18}}>{ct?.icon||'📦'}</span>
                    <div style={{flex:1,minWidth:0}}>
                      <p title={c.description||ct?.label} style={{fontSize:12,fontWeight:600,color:T.navy,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{c.description||ct?.label}</p>
                      <p style={{fontSize:10,color:T.muted}}>{fmtDate(c.postingDate)}</p>
                    </div>
                    <div style={{textAlign:'right',flexShrink:0}}>
                      <p style={{fontSize:13,fontWeight:700,color:T.navy}}>{fmtCurrency(c.amount,currency)}</p>
                      {isAccom && dVat > 0 && (
                        <p style={{fontSize:10,color:T.muted}}>
                          VAT {fmtCurrency(dVat,currency)} + Tourism {fmtCurrency(dTourism,currency)}
                        </p>
                      )}
                      {!isAccom && dVatOnly > 0 && (
                        <p style={{fontSize:10,color:T.muted}}>incl. VAT {fmtCurrency(dVatOnly,currency)}</p>
                      )}
                    </div>
                    <button onClick={()=>handleVoid(c.id)} title="Void charge"
                      style={{background:'none',border:'none',cursor:'pointer',color:T.danger,fontSize:14,padding:4}}>🗑️</button>
                  </div>
                );
              })
            }
          </div>
          {voidedChgs.length>0&&(
            <div style={{padding:'8px 18px',background:T.surface,borderTop:`1px solid ${T.border}`}}>
              <p style={{fontSize:10,color:T.muted}}>{voidedChgs.length} voided charge{voidedChgs.length>1?'s':''} excluded</p>
            </div>
          )}
          <div style={{padding:'12px 18px',background:T.surface,borderTop:`1px solid ${T.border}`,display:'flex',justifyContent:'space-between'}}>
            <span style={{fontSize:12,fontWeight:700,color:T.slate}}>Total</span>
            <span style={{fontSize:14,fontWeight:700,color:T.navy}}>{fmtCurrency(totalCharges,currency)}</span>
          </div>
        </Card>

        {/* Payments */}
        <Card style={{padding:0,overflow:'hidden'}}>
          <div style={{padding:'14px 18px',borderBottom:`1px solid ${T.border}`,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
            <p style={{fontSize:13,fontWeight:700,color:T.navy}}>Payments ({payments.length})</p>
            <Btn size="sm" variant="gold" onClick={()=>setShowPay(true)}>+ Record</Btn>
          </div>
          <div style={{overflowY:'auto',maxHeight:380}}>
            {allPayments.length===0
              ?<p style={{textAlign:'center',padding:30,color:T.muted,fontSize:12}}>No payments recorded</p>
              :allPayments.map((p,i)=>{
                const pm=PAYMENT_METHODS.find(m=>m.id===p.method);
                const isNeg=parseFloat(p.amount)<0||p.reversed;
                return(
                  <div key={p.id||i} style={{padding:'11px 18px',borderBottom:`1px solid ${T.border}`,display:'flex',alignItems:'center',gap:10,
                    background:isNeg?T.dangerPale:'transparent'}}>
                    <span style={{fontSize:18}}>{isNeg?'↩️':pm?.icon||'💰'}</span>
                    <div style={{flex:1,minWidth:0}}>
                      <p style={{fontSize:12,fontWeight:600,color:isNeg?T.danger:T.navy}}>
                        {isNeg?'['+( p.notes?.includes('REFUND')?'REFUND':'REVERSAL')+'] ':''}{pm?.label||p.method}
                      </p>
                      <p style={{fontSize:10,color:T.muted}}>{fmtDateTime(p.paidAt)}{p.transactionRef?` · ${p.transactionRef}`:''}</p>
                    </div>
                    <p style={{fontSize:13,fontWeight:700,color:isNeg?T.danger:T.success}}>{fmtCurrency(p.amount,currency)}</p>
                    {!isNeg&&!p.reversed&&(
                      <button onClick={()=>setShowReverse(p)} title="Reverse payment"
                        style={{background:'none',border:'none',cursor:'pointer',color:T.warning,fontSize:14,padding:4}}>↩️</button>
                    )}
                  </div>
                );
              })
            }
          </div>
          <div style={{padding:'12px 18px',background:T.surface,borderTop:`1px solid ${T.border}`,display:'flex',justifyContent:'space-between'}}>
            <span style={{fontSize:12,fontWeight:700,color:T.slate}}>Net Paid</span>
            <span style={{fontSize:14,fontWeight:700,color:totalPaid>=0?T.success:T.danger}}>{fmtCurrency(totalPaid,currency)}</span>
          </div>
        </Card>
      </div>

      {/* ── Add Charge Modal ── */}
      <Modal open={showCharge} onClose={()=>setShowCharge(false)} title="+ Post Charge">
        <Field label="Charge Type" required>
          <Select value={charge.chargeType} onChange={v=>setCharge(p=>({...p,chargeType:v}))}>
            {CHARGE_TYPES.map(c=><option key={c.id} value={c.id}>{c.icon} {c.label}</option>)}
          </Select>
        </Field>
        <Field label="Description"><Input value={charge.description} onChange={v=>setCharge(p=>({...p,description:v}))} placeholder="Optional description"/></Field>
        {isCorporateReservation && (
          <div style={{fontSize:10.5,color:T.teal,background:`${T.teal}10`,border:`1px solid ${T.teal}30`,
            borderRadius:8,padding:'7px 11px',marginBottom:10}}>
            🏢 {corpChargeTariffLoading ? 'Checking contracted price…'
              : corpChargeTariff ? `Contracted price applied for ${reservation.corporateAccountName || 'this company'} — amount prefilled below, editable if needed`
              : `${reservation.corporateAccountName || 'This company'} has no negotiated price for ${CHARGE_TYPES.find(c=>c.id===charge.chargeType)?.label || charge.chargeType} — enter manually`}
          </div>
        )}
        <Field label={`Amount (${currency})`} required>
          <Input type="number" min="0" value={charge.amount} onChange={v=>setCharge(p=>({...p,amount:v}))} placeholder="0.00"/>
        </Field>

        {/* Tax is resolved automatically by category — staff don't need to know
            or type a percentage. Accommodation carries BOTH the Tourism Levy
            and VAT at once; every other service carries VAT only. An explicit
            override is available for edge cases (comp, tax-exempt). The amount
            entered above is the full amount the guest is charged; the lines
            below just show how much of it is tax, for reference. */}
        {(() => {
          const auto = previewTax(charge.chargeType, charge.amount, property);
          const amt = parseFloat(charge.amount||0) || 0;
          const isAccommodation = charge.chargeType === 'room';
          let tourismTax = auto.tourismTax, vatTax = auto.vatTax;
          if (charge.taxOverride) {
            const overrideRate = parseFloat(charge.taxRate||0) || 0;
            tourismTax = 0;
            vatTax = overrideRate > 0 ? round2(amt * overrideRate / (100 + overrideRate)) : 0;
          }
          const taxPortion = round2(tourismTax + vatTax);
          return (
            <div style={{background:T.surface,borderRadius:10,padding:'10px 14px',marginBottom:10}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:charge.taxOverride?10:0}}>
                <span style={{fontSize:11,color:T.muted}}>
                  Tax ({auto.category}{!charge.taxOverride && isAccommodation ? ` · ${auto.tourismRate}% Tourism + ${auto.vatRate}% VAT` : ''}{!charge.taxOverride && !isAccommodation ? ` · ${auto.vatRate}% VAT` : ''})
                </span>
                <label style={{display:'flex',alignItems:'center',gap:5,fontSize:10,color:T.muted,cursor:'pointer'}}>
                  <input type="checkbox" checked={charge.taxOverride}
                    onChange={e=>setCharge(p=>({...p,taxOverride:e.target.checked,taxRate:e.target.checked?String(auto.taxRate):''}))}/>
                  Override
                </label>
              </div>
              {charge.taxOverride && (
                <Field label="Custom Tax Rate %">
                  <Input type="number" min="0" max="100" value={charge.taxRate}
                    onChange={v=>setCharge(p=>({...p,taxRate:v}))} placeholder="0"/>
                </Field>
              )}
              {charge.amount && (
                <>
                  {!charge.taxOverride && isAccommodation && (
                    <>
                      <div style={{display:'flex',justifyContent:'space-between',fontSize:11,color:T.muted,marginTop:6}}>
                        <span>Tourism Levy ({auto.tourismRate}%):</span>
                        <span>{fmtCurrency(tourismTax, currency)}</span>
                      </div>
                      <div style={{display:'flex',justifyContent:'space-between',fontSize:11,color:T.muted,marginTop:2}}>
                        <span>VAT ({auto.vatRate}%):</span>
                        <span>{fmtCurrency(vatTax, currency)}</span>
                      </div>
                    </>
                  )}
                  {(charge.taxOverride || !isAccommodation) && (
                    <div style={{display:'flex',justifyContent:'space-between',fontSize:11,color:T.muted,marginTop:charge.taxOverride?8:6}}>
                      <span>Of which tax is:</span>
                      <span>{fmtCurrency(taxPortion, currency)}</span>
                    </div>
                  )}
                  <div style={{display:'flex',justifyContent:'space-between',fontSize:13,fontWeight:700,color:T.navy,marginTop:4}}>
                    <span>Total charged to guest:</span>
                    <span>{fmtCurrency(amt, currency)}</span>
                  </div>
                </>
              )}
            </div>
          );
        })()}

        <div style={{display:'flex',gap:10,marginTop:8}}>
          <Btn variant="secondary" onClick={()=>setShowCharge(false)} style={{flex:1}}>Cancel</Btn>
          <Btn variant="primary" disabled={saving} onClick={handleCharge} style={{flex:2}}>{saving?'Posting…':'Post Charge'}</Btn>
        </div>
      </Modal>

      {/* ── Payment Modal ── */}
      <Modal open={showPay} onClose={()=>{setShowPay(false);setMomoSt('idle');}} title="💳 Record Payment" width={560}>
        <Field label="Payment Method" required>
          <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:8,marginBottom:8}}>
            {PAYMENT_METHODS.map(m=>(
              <button key={m.id} onClick={()=>setPayment(p=>({...p,method:m.id}))}
                style={{border:`2px solid ${payment.method===m.id?T.teal:T.border}`,borderRadius:10,padding:'8px 6px',cursor:'pointer',
                  background:payment.method===m.id?T.tealPale:T.white,display:'flex',flexDirection:'column',alignItems:'center',gap:3}}>
                <span style={{fontSize:20}}>{m.icon}</span>
                <span style={{fontSize:9,fontWeight:600,color:payment.method===m.id?T.teal:T.muted,textAlign:'center'}}>{m.label}</span>
              </button>
            ))}
          </div>
        </Field>
        <div style={{display:'grid',gridTemplateColumns:'3fr 2fr',gap:12}}>
          <Field label={`Amount (${currency})`} required>
            <Input type="number" min="0" value={payment.amount}
              onChange={v=>setPayment(p=>({...p,amount:v}))}
              placeholder={balance>0?`Balance: ${fmtCurrency(balance,currency)}`:'0.00'}/>
          </Field>
          <Field label="Reference">
            <Input value={payment.transactionRef} onChange={v=>setPayment(p=>({...p,transactionRef:v}))} placeholder="Optional"/>
          </Field>
        </div>
        {balance>0&&!payment.amount&&(
          <button onClick={()=>setPayment(p=>({...p,amount:String(balance.toFixed(2))}))}
            style={{background:'none',border:`1px dashed ${T.teal}`,color:T.teal,borderRadius:8,padding:'5px 12px',fontSize:11,cursor:'pointer',fontFamily:FONTS.body,marginBottom:8}}>
            Set to full balance: {fmtCurrency(balance,currency)}
          </button>
        )}
        {(payment.method==='mtn_momo'||payment.method==='airtel')&&(
          <div style={{background:T.warningPale,border:`1px solid ${T.warning}30`,borderRadius:10,padding:'12px 14px',marginBottom:8}}>
            <p style={{fontSize:12,fontWeight:700,color:T.warning,marginBottom:8}}>
              {payment.method==='mtn_momo'?'📱 MTN MoMo':'📲 Airtel Money'} — Push to Pay
            </p>
            {momoSt==='idle'&&(
              <>
                <Field label="Customer Phone">
                  <Input value={momoPhone} onChange={setMomoPhone} placeholder="+250 7xx xxx xxx"/>
                </Field>
                <Btn variant="gold" size="sm" disabled={!momoPhone||!payment.amount} onClick={triggerMoMo}>Send Push</Btn>
              </>
            )}
            {momoSt==='pending'&&<div style={{display:'flex',alignItems:'center',gap:8,color:T.warning}}><Spinner size={18} color={T.warning}/><span style={{fontSize:12}}>Waiting for approval…</span></div>}
            {momoSt==='success'&&<p style={{fontSize:12,color:T.success,fontWeight:700}}>✅ Confirmed!</p>}
          </div>
        )}
        <div style={{display:'flex',gap:10,marginTop:12}}>
          <Btn variant="secondary" onClick={()=>setShowPay(false)} style={{flex:1}}>Cancel</Btn>
          {payment.method!=='mtn_momo'&&payment.method!=='airtel'&&(
            <Btn variant="primary" disabled={saving} onClick={()=>handlePayment()} style={{flex:2}}>
              {saving?'Saving…':`Record ${payment.method==='cash'?'💵':'💳'} Payment`}
            </Btn>
          )}
        </div>
      </Modal>

      {/* ── Refund Modal ── */}
      <Modal open={showRefund} onClose={()=>setShowRefund(false)} title="↩️ Refund Guest" width={480}>
        <div style={{background:T.warningPale,border:`1px solid ${T.warning}30`,borderRadius:10,padding:'10px 14px',marginBottom:16}}>
          <p style={{fontSize:12,color:T.warning}}>
            ℹ️ A refund records a negative payment. This reduces the net amount paid and creates a full audit trail.
            For error corrections, use <strong>Reverse Payment</strong> on the specific payment instead.
          </p>
        </div>
        <Field label={`Refund Amount (${currency})`} required>
          <Input type="number" min="0" value={refund.amount} onChange={v=>setRefund(p=>({...p,amount:v}))} placeholder="0.00"/>
        </Field>
        <Field label="Refund Method">
          <Select value={refund.method} onChange={v=>setRefund(p=>({...p,method:v}))}>
            {PAYMENT_METHODS.filter(m=>!['comp','voucher'].includes(m.id)).map(m=><option key={m.id} value={m.id}>{m.label}</option>)}
          </Select>
        </Field>
        <Field label="Reason">
          <Input value={refund.reason} onChange={v=>setRefund(p=>({...p,reason:v}))} placeholder="Reason for refund…"/>
        </Field>
        <Field label="Reference / Auth Code">
          <Input value={refund.reference} onChange={v=>setRefund(p=>({...p,reference:v}))} placeholder="Optional bank/MoMo reference"/>
        </Field>
        <div style={{display:'flex',gap:10,marginTop:16}}>
          <Btn variant="secondary" onClick={()=>setShowRefund(false)} style={{flex:1}}>Cancel</Btn>
          <Btn variant="danger" disabled={saving} onClick={handleRefund} style={{flex:2}}>{saving?'Processing…':'↩️ Record Refund'}</Btn>
        </div>
      </Modal>

      {/* ── Reverse Payment Modal ── */}
      <Modal open={!!showReverse} onClose={()=>setShowReverse(null)} title="↩️ Reverse Payment" width={440}>
        {showReverse&&(
          <>
            <div style={{background:T.dangerPale,border:`1px solid ${T.danger}30`,borderRadius:10,padding:'12px 14px',marginBottom:16}}>
              <p style={{fontSize:12,color:T.danger,marginBottom:4}}>
                <strong>Payment to reverse:</strong> {fmtCurrency(showReverse.amount,currency)} via {showReverse.method?.replace('_',' ')} ({fmtDateTime(showReverse.paidAt)})
              </p>
              <p style={{fontSize:11,color:T.muted}}>
                This creates a negative entry to cancel the payment. The original record is preserved for audit.
                Use this for error corrections only — for customer-requested money-back, use <strong>Refund</strong> instead.
              </p>
            </div>
            <Field label="Reason for Reversal" required>
              <Input value={showReverse.reverseReason||''} onChange={v=>setShowReverse(p=>({...p,reverseReason:v}))} placeholder="e.g. Duplicate payment, wrong amount…"/>
            </Field>
            <div style={{display:'flex',gap:10,marginTop:16}}>
              <Btn variant="secondary" onClick={()=>setShowReverse(null)} style={{flex:1}}>Cancel</Btn>
              <Btn variant="danger" disabled={saving} onClick={()=>handleReverse(showReverse)} style={{flex:2}}>{saving?'Processing…':'↩️ Reverse Payment'}</Btn>
            </div>
          </>
        )}
      </Modal>

      {/* ── Receipt Modal ── */}
      <Modal open={showReceipt} onClose={()=>setShowReceipt(false)} title="🖨️ Folio Receipt" width={400}>
        {folio&&<ReceiptPrint folio={folio} reservation={reservation} property={property} onClose={()=>setShowReceipt(false)}/>}
      </Modal>

      {/* ── Invoice Modal ── */}
      <Modal open={showInvoice} onClose={()=>setShowInvoice(false)} title="🧾 Invoice" width={780}>
        <InvoicePrint folio={folio} reservation={reservation} property={property} onClose={()=>setShowInvoice(false)}/>
      </Modal>

      {/* ── Payment Request Modal ── */}
      <Modal open={showPaymentRequest} onClose={()=>setShowPaymentRequest(false)} title="📨 Payment Request" width={740}>
        <PaymentRequestPrint folio={folio} reservation={reservation} property={property} onClose={()=>setShowPaymentRequest(false)}/>
      </Modal>

      <Toast message={toast}/>
    </div>
  );
}